import * as React from 'react'

import { TBannerContainerProps } from './BannerContainer'

type Breakpoint = '@initial' | '@sm' | '@md' | '@lg' | '@xl'
type ResponsiveSize = 'sm' | 'md' | Partial<Record<Breakpoint, 'sm' | 'md'>>

// Extract size and emphasis props, preserving responsive variant types
export type TBannerProviderProps = {
  size?: ResponsiveSize
  emphasis?: TBannerContainerProps['emphasis']
}

type TBannerContextValue = TBannerProviderProps & {
  hasDismiss: boolean
  setHasDismiss: React.Dispatch<React.SetStateAction<boolean>>
}

export const BannerContext = React.createContext<TBannerContextValue>({
  emphasis: 'minimal',
  size: {
    '@initial': 'sm',
    '@md': 'md'
  } as unknown as TBannerProviderProps['size'],
  hasDismiss: false,
  setHasDismiss: () => null
})

export const useBannerContext = (): TBannerContextValue => {
  const context = React.useContext(BannerContext)

  if (context === undefined) {
    throw new Error('useBannerContext must be used within a BannerProvider')
  }

  return context
}

export const BannerProvider = ({
  emphasis = 'minimal',
  size = {
    '@initial': 'sm',
    '@md': 'md'
  } as unknown as TBannerProviderProps['size'],
  children
}: React.PropsWithChildren<TBannerProviderProps>) => {
  const [hasDismiss, setHasDismiss] = React.useState(false)
  const value = React.useMemo<TBannerContextValue>(
    () => ({ emphasis, size, hasDismiss, setHasDismiss }),
    [emphasis, size, hasDismiss, setHasDismiss]
  )
  return (
    <BannerContext.Provider value={value}>{children}</BannerContext.Provider>
  )
}

BannerProvider.displayName = 'Banner'
